<template>
    <div class="relative" ref="containerRef">
        <label
            v-if="label"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
            {{ label }} <span v-if="required" class="text-danger-500">*</span>
        </label>

        <!-- Selected Value Display / Search Input -->
        <div
            ref="triggerRef"
            @click="toggleDropdown"
            class="w-full px-3 py-2 border rounded-md cursor-pointer flex items-center justify-between dark:bg-gray-700 dark:text-white transition-colors"
            :class="{
                'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500':
                    !error,
                'border-danger-500': error,
                'bg-gray-100 dark:bg-gray-600': disabled,
            }"
        >
            <span v-if="selectedOption" class="truncate">
                {{ displayValue }}
            </span>
            <span v-else class="text-gray-500 dark:text-gray-400 truncate">
                {{ placeholder }}
            </span>
            <svg
                class="h-4 w-4 ml-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                />
            </svg>
        </div>

        <!-- Error Message -->
        <p
            v-if="error && error.length > 0"
            class="mt-1 text-sm text-danger-600"
        >
            {{ Array.isArray(error) ? error[0] : error }}
        </p>

        <!-- Dropdown (teleported to body so modal overflow panels never clip it) -->
        <Teleport to="body">
            <div
                v-if="isOpen"
                @click="closeDropdown"
                class="fixed inset-0 z-40"
            ></div>
            <div
                v-if="isOpen"
                ref="dropdownRef"
                class="fixed z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg overflow-y-auto"
                :style="dropdownStyle"
            >
                <!-- Search Input -->
                <div class="p-2 border-b border-gray-200 dark:border-gray-700">
                    <input
                        ref="searchInputRef"
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search..."
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                </div>

                <!-- Options List -->
                <div v-if="filteredOptions.length > 0">
                    <div
                        v-for="option in filteredOptions"
                        :key="getOptionKey(option)"
                        @click="selectOption(option)"
                        class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        :class="{
                            'bg-primary-50 dark:bg-primary-900/30':
                                isSelected(option),
                        }"
                    >
                        {{ getOptionLabel(option) }}
                    </div>
                </div>
                <div
                    v-else
                    class="px-3 py-4 text-center text-gray-500 dark:text-gray-400 text-sm"
                >
                    No results found
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    reactive,
    computed,
    watch,
    onMounted,
    onUnmounted,
    nextTick,
} from "vue";

interface Props {
    modelValue: any;
    options: any[];
    label?: string;
    placeholder?: string;
    optionLabel?: string | ((option: any) => string);
    optionValue?: string | ((option: any) => any);
    required?: boolean;
    disabled?: boolean;
    error?: string | string[];
}

interface Emits {
    (e: "update:modelValue", value: any): void;
}

const props = withDefaults(defineProps<Props>(), {
    label: "",
    placeholder: "Select an option",
    required: false,
    disabled: false,
    error: "",
});

const emit = defineEmits<Emits>();

const isOpen = ref(false);
const searchQuery = ref("");
const containerRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);

// Fixed positioning for the teleported dropdown, computed from the trigger
const MAX_DROPDOWN_HEIGHT = 288; // search box + max-h-60 options
const MIN_USABLE_HEIGHT = 140;
const dropdownStyle = reactive<Record<string, string>>({});

const updateDropdownPosition = () => {
    const trigger = triggerRef.value;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;

    if (spaceBelow >= MIN_USABLE_HEIGHT || spaceBelow >= spaceAbove) {
        // Open downward
        dropdownStyle.top = `${rect.bottom + 4}px`;
        dropdownStyle.bottom = "auto";
        dropdownStyle.maxHeight = `${Math.max(
            Math.min(MAX_DROPDOWN_HEIGHT, spaceBelow),
            MIN_USABLE_HEIGHT,
        )}px`;
    } else {
        // Open upward
        dropdownStyle.top = "auto";
        dropdownStyle.bottom = `${window.innerHeight - rect.top + 4}px`;
        dropdownStyle.maxHeight = `${Math.max(
            Math.min(MAX_DROPDOWN_HEIGHT, spaceAbove),
            MIN_USABLE_HEIGHT,
        )}px`;
    }
    dropdownStyle.left = `${rect.left}px`;
    dropdownStyle.width = `${rect.width}px`;
};

// Get the label for an option
const getOptionLabel = (option: any): string => {
    if (typeof props.optionLabel === "function") {
        return props.optionLabel(option);
    }
    if (props.optionLabel) {
        return option[props.optionLabel];
    }
    // Default: try to find a reasonable label
    return (
        option.name ||
        option.title ||
        option.display_name ||
        option.user?.name ||
        option.label ||
        String(option)
    );
};

// Get the value for an option
const getOptionValue = (option: any): any => {
    if (typeof props.optionValue === "function") {
        return props.optionValue(option);
    }
    if (props.optionValue) {
        return option[props.optionValue];
    }
    // Default: use id or the option itself
    return option.id || option;
};

// Get a unique key for an option
const getOptionKey = (option: any): string => {
    return String(getOptionValue(option));
};

// Check if an option is selected
const isSelected = (option: any): boolean => {
    return getOptionValue(option) === props.modelValue;
};

// Get the currently selected option
const selectedOption = computed(() => {
    if (props.modelValue === undefined || props.modelValue === null)
        return null;
    return props.options.find(
        (opt) => getOptionValue(opt) === props.modelValue,
    );
});

// Get the display value for the selected option
const displayValue = computed(() => {
    if (!selectedOption.value) return "";
    return getOptionLabel(selectedOption.value);
});

// Filter options based on search query
const filteredOptions = computed(() => {
    if (!searchQuery.value.trim()) {
        return props.options;
    }

    const query = searchQuery.value.toLowerCase();
    return props.options.filter((option) => {
        const label = getOptionLabel(option).toLowerCase();
        return label.includes(query);
    });
});

// Toggle dropdown
const toggleDropdown = () => {
    if (props.disabled) return;
    if (isOpen.value) {
        closeDropdown();
        return;
    }
    isOpen.value = true;
    updateDropdownPosition();
    // Only auto-focus the search input on precise pointers (desktop):
    // on touch devices the on-screen keyboard would cover the options.
    if (window.matchMedia("(pointer: fine)").matches) {
        nextTick(() => {
            searchInputRef.value?.focus();
        });
    }
};

// Close dropdown
const closeDropdown = () => {
    isOpen.value = false;
    searchQuery.value = "";
};

// Select an option
const selectOption = (option: any) => {
    emit("update:modelValue", getOptionValue(option));
    closeDropdown();
};

// Handle click outside
const handleClickOutside = (event: MouseEvent) => {
    if (
        containerRef.value &&
        !containerRef.value.contains(event.target as Node)
    ) {
        closeDropdown();
    }
};

// Handle escape key
const handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
        closeDropdown();
    }
};

// Keep the dropdown anchored to the trigger while anything scrolls/resizes
const handleReposition = () => {
    if (isOpen.value) {
        updateDropdownPosition();
    }
};

// Watch for model value changes to reset search
watch(
    () => props.modelValue,
    () => {
        searchQuery.value = "";
    },
);

// Lifecycle hooks
onMounted(() => {
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
    document.removeEventListener("keydown", handleEscape);
    window.removeEventListener("resize", handleReposition);
    window.removeEventListener("scroll", handleReposition, true);
});
</script>

<style scoped>
/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
    width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    @apply bg-gray-100;
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
    @apply bg-gray-700;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    @apply bg-gray-400;
    border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-600;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
    @apply bg-gray-500;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
}
</style>
